import { Injectable, OnDestroy } from '@angular/core';
import { HeaderComponent } from 'shared/components';
import { Data } from '@angular/router';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../util/unsubscribeFromAll';
import { RouteHelperService } from './route-helper.service';
import { Breadcrumb } from '../interfaces/breadcrumb.interface';
import clone from 'lodash-es/clone';

@Injectable({
    providedIn: 'root',
})
export class HeaderService implements OnDestroy {
    private headerReference!: HeaderComponent;
    private originalRouteBreadcrumbs!: Breadcrumb[];
    private subscriptions: { [key: string]: Subscription } = {};

    constructor(private routeHelperService: RouteHelperService) {
        this.subscriptions.routeData = this.routeHelperService.getFinalRouteData().subscribe((data: Data) => {
            this.clearBreadcrumbs();
            this.setUserMenuVisibility(true);
            this.setNotificationsMenuVisibility(true);
            this.originalRouteBreadcrumbs = [];

            if (data && data['header']) {
                if (data['header'].breadcrumbs) {
                    this.originalRouteBreadcrumbs = data['header'].breadcrumbs.slice();
                    this.setBreadcrumbs(data['header'].breadcrumbs);
                }
                if (data['header'].disableUserMenu !== undefined) {
                    this.setUserMenuVisibility(!data['header'].disableUserMenu);
                }

                if (data['header'].disableNotificationsMenu !== undefined) {
                    this.setNotificationsMenuVisibility(!data['header'].disableNotificationsMenu);
                }
            }
        });
    }

    public ngOnDestroy(): void {
        unsubscribeFromAll(this.subscriptions);
    }

    public registerHeader(reference: HeaderComponent) {
        this.headerReference = reference;
    }

    private headerIsRegistered() {
        return this.headerReference !== undefined;
    }

    public setUserMenuVisibility(visible: boolean): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        this.headerReference.userMenuVisible = visible;
    }

    public setNotificationsMenuVisibility(visible: boolean): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        this.headerReference.notificationsMenuVisible = visible;
    }

    public setBreadcrumbs(breadcrumbs: Breadcrumb[]): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        this.headerReference.breadcrumbs = breadcrumbs.slice();
    }

    public clearBreadcrumbs(): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        this.headerReference.breadcrumbs = [];
    }

    public addToBreadcrumbs(breadcrumbs: Breadcrumb | Breadcrumb[]): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        if (!Array.isArray(breadcrumbs)) {
            breadcrumbs = [breadcrumbs];
        }

        for (let i = 0; i < breadcrumbs.length; i++) {
            const breadcrumb = breadcrumbs[i];

            this.headerReference.breadcrumbs.push(breadcrumb);
        }
    }

    public popBreadcrumb(): Breadcrumb | undefined {
        if (!this.headerIsRegistered()) {
            return;
        }

        if (!this.headerReference.breadcrumbs || this.headerReference.breadcrumbs.length === 0) {
            return;
        }

        return this.headerReference.breadcrumbs.pop();
    }

    public getAllBreadcrumbs(): Breadcrumb[] | undefined {
        if (!this.headerIsRegistered()) {
            return;
        }

        if (!this.headerReference.breadcrumbs || this.headerReference.breadcrumbs.length === 0) {
            return;
        }

        return clone(this.headerReference.breadcrumbs);
    }

    public resetBreadcrumbsToRouteDefault(): void {
        if (!this.headerIsRegistered()) {
            return;
        }

        this.headerReference.breadcrumbs = this.originalRouteBreadcrumbs.slice();
    }
}
